/**
 * O2 — Reasoning panel. The differentiator surface: watch the AI think.
 *
 * Bottom-anchored DOM panel per side, time-synced to the current
 * playback turn. Renders the Decide-phase `reasoning_text` and the
 * tool calls the LLM emitted for that turn.
 *
 * Lookup semantics:
 *   - Primary: exact-match `phase === "decide" && turn === currentTurn`
 *   - Fallback: most recent Decide-phase entry at or before currentTurn
 *     (covers ticks where the agent skipped a Decide, e.g. shutdown,
 *     run-out-of-time). The fallback variant tags itself "T{n} →" so
 *     the spectator knows the displayed reasoning isn't from THIS turn.
 *
 * Empty states (renders an explicit placeholder, not blank):
 *   - No transcript loaded → "—" + "(transcript unavailable)"
 *   - No Decide entry at or before currentTurn → "—" + "(pre-deploy)"
 *   - Decide entry exists but `response.reasoning_text` is absent →
 *     show the tool calls only (some models don't emit reasoning)
 *
 * Visibility gating (privacy contract — see transcript-transport.md §9):
 *   - The overlay package renders whatever entries the consumer passes.
 *   - The consumer is responsible for fetching only transcripts the
 *     current viewer is authorized to see (engineer:self vs published).
 *   - For OBS routes (O4), pre-resolve published-only transcripts
 *     before passing them down — the overlay doesn't enforce auth.
 */

import type { CSSProperties, ReactNode } from "react";
import { Panel } from "./Panel";
import {
  decidePhaseAt,
  formatToolCall,
  lastDecidePhaseAtOrBefore,
  type TranscriptEntry,
} from "./transcript";

export type ReasoningAnchor = "left" | "right";

export interface ReasoningPanelProps {
  /** Per-side transcript entries (typically the full match transcript
   *  for one Agent). Order doesn't matter — lookup is by phase + turn. */
  transcript: ReadonlyArray<TranscriptEntry>;
  /** Current playback turn (mirrors `frame.tick` from the renderer). */
  turn: number;
  /** Side label / accent. */
  side: "A" | "B";
  /** Agent display name (header). */
  name: string;
  /** Anchor; defaults to A → left, B → right. */
  anchor?: ReasoningAnchor;
  /** Bottom offset in px; useful when stacking under a telemetry feed. */
  bottomOffset?: number;
  /** Side accent override; defaults to the token. */
  accent?: string;
  /** Show the LLM stats row (model · tokens · latency). */
  showStats?: boolean;
  /** Custom empty-state node for "transcript unavailable" (consumer
   *  may want a "viewer not signed in" CTA, etc.). */
  unavailablePlaceholder?: ReactNode;
  /** Hide entirely. */
  hidden?: boolean;
}

const rootStyle = (anchor: ReasoningAnchor, bottomOffset: number): CSSProperties => ({
  position: "absolute",
  bottom: bottomOffset,
  [anchor]: "var(--pp-edge-gutter)",
  width: "var(--pp-reasoning-panel-w)",
  // Root blocks pointer events so the panel doesn't steal clicks from
  // the 3D scene; the scrollable reasoning body re-enables them so the
  // spectator can scrub long thoughts. Audit fix.
  pointerEvents: "none",
  userSelect: "none",
});

const contentStyle: CSSProperties = {
  padding: "var(--pp-padding)",
  display: "flex",
  flexDirection: "column",
  gap: "var(--pp-gap)",
  color: "var(--pp-text)",
  fontFamily: "var(--pp-font-family)",
  fontSize: "var(--pp-font-md)",
  fontWeight: "var(--pp-weight-normal)",
};

const headerStyle = (accentColor: string): CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  paddingBottom: 6,
  borderBottom: `1px solid ${accentColor}`,
});

const sideTagStyle = (accentColor: string): CSSProperties => ({
  padding: "1px 6px",
  background: accentColor,
  color: "var(--pp-text-on-accent)",
  fontWeight: "var(--pp-weight-bold)",
  fontSize: "var(--pp-font-sm)",
  letterSpacing: "var(--pp-tracking-caps)",
});

const nameStyle: CSSProperties = {
  fontWeight: "var(--pp-weight-bold)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  flex: 1,
  minWidth: 0,
};

const turnTagStyle: CSSProperties = {
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text-muted)",
  textTransform: "uppercase",
  letterSpacing: "var(--pp-tracking-caps)",
  fontVariantNumeric: "tabular-nums",
};

const reasoningBodyStyle: CSSProperties = {
  maxHeight: "var(--pp-reasoning-max-h)",
  overflowY: "auto",
  fontSize: "var(--pp-font-md)",
  lineHeight: 1.45,
  color: "var(--pp-text)",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  // Re-enable pointer events on the scroller so spectators can scrub
  // long thoughts; the panel root sets pointer-events:none for the
  // chrome itself. Audit fix.
  pointerEvents: "auto",
  // Subtle fade at the bottom so long reasoning blocks visually
  // bleed off rather than abruptly truncating.
  maskImage: "var(--pp-fade-mask-bottom)",
  WebkitMaskImage: "var(--pp-fade-mask-bottom)",
};

const toolListStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 3,
  borderTop: "1px solid var(--pp-border)",
  paddingTop: 6,
};

const toolListHeaderStyle: CSSProperties = {
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text-muted)",
  textTransform: "uppercase",
  letterSpacing: "var(--pp-tracking-caps)",
};

const toolCallStyle: CSSProperties = {
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text)",
  fontFamily: "var(--pp-font-family)",
  wordBreak: "break-all",
};

const statsRowStyle: CSSProperties = {
  display: "flex",
  gap: 10,
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text-muted)",
  borderTop: "1px solid var(--pp-border)",
  paddingTop: 6,
  fontVariantNumeric: "tabular-nums",
};

const placeholderStyle: CSSProperties = {
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text-muted)",
  fontStyle: "italic",
  padding: "8px 0",
};

function defaultAnchor(side: "A" | "B"): ReasoningAnchor {
  return side === "A" ? "left" : "right";
}

function defaultAccent(side: "A" | "B"): string {
  return side === "A" ? "var(--pp-accent-a)" : "var(--pp-accent-b)";
}

export function ReasoningPanel({
  transcript,
  turn,
  side,
  name,
  anchor = defaultAnchor(side),
  bottomOffset = 12,
  accent,
  showStats = false,
  unavailablePlaceholder,
  hidden = false,
}: ReasoningPanelProps) {
  if (hidden) return null;
  const accentColor = accent ?? defaultAccent(side);

  // Exact match wins; falls back to most-recent-before for ticks where
  // no Decide fired this turn (shutdown / timeout / pre-deploy).
  const exact = decidePhaseAt(transcript, turn);
  const entry = exact ?? lastDecidePhaseAtOrBefore(transcript, turn);
  const isStale = exact === null && entry !== null;

  const transcriptEmpty = transcript.length === 0;
  const reasoningText = entry?.response?.reasoning_text?.trim();
  const toolCalls = entry?.response?.tool_calls ?? [];
  const showToolList = toolCalls.length > 0;
  const showBody = Boolean(reasoningText) || showToolList;

  return (
    <div style={rootStyle(anchor, bottomOffset)} aria-label={`Side ${side} reasoning`}>
      <Panel accent={accentColor}>
        <div style={contentStyle}>
          <div style={headerStyle(accentColor)}>
            <span style={sideTagStyle(accentColor)}>{side}</span>
            <span style={nameStyle} title={name}>
              {name}
            </span>
            <span style={turnTagStyle}>
              {entry === null
                ? `T${turn.toString().padStart(2, "0")}`
                : isStale
                  ? `T${entry.turn.toString().padStart(2, "0")} → T${turn.toString().padStart(2, "0")}`
                  : `T${entry.turn.toString().padStart(2, "0")} · DECIDE`}
            </span>
          </div>

          {transcriptEmpty ? (
            <div style={placeholderStyle}>
              {unavailablePlaceholder ?? "— (transcript unavailable)"}
            </div>
          ) : entry === null ? (
            <div style={placeholderStyle}>— (pre-deploy)</div>
          ) : !showBody ? (
            <div style={placeholderStyle}>— (no reasoning emitted)</div>
          ) : (
            <>
              {reasoningText ? (
                <div style={reasoningBodyStyle}>{reasoningText}</div>
              ) : null}
              {showToolList ? (
                <div style={toolListStyle}>
                  <span style={toolListHeaderStyle}>DECISION</span>
                  {toolCalls.map((tc, i) => (
                    <span key={i} style={toolCallStyle}>
                      › {formatToolCall(tc)}
                    </span>
                  ))}
                </div>
              ) : null}
            </>
          )}

          {showStats && entry !== null ? (
            <div style={statsRowStyle}>
              {entry.llm?.model ? <span>{entry.llm.model}</span> : null}
              {entry.response?.tokens_out !== undefined ? (
                <span>{entry.response.tokens_out} tok</span>
              ) : null}
              {entry.response?.latency_ms !== undefined ? (
                <span>{entry.response.latency_ms} ms</span>
              ) : null}
            </div>
          ) : null}
        </div>
      </Panel>
    </div>
  );
}
